<template>
  <div class="splide-wrapper">
    <splide
      class="main"
      ref="primary"
      :options="options"
      @on-track-click="() => toggleModal(false)"
      @splide:moved="onSplideMoved"
    >
      <splide-slide v-for="video in videos" :key="video.thumbnail">
        <imgix-video ref="video" :autoplay="true" :video="video" />
      </splide-slide>
      <splide-slide
        v-for="(item, index) in items"
        :key="`item-${index}-${item.src}`"
      >
        <figure class="media-gallery__figure">
          <ix-img
            :alt="item.text || ''"
            :imgix-params="{
              h: maxImageSize,
              w: maxImageSize,
            }"
            :src="item.src"
          />
          <figcaption
            class="media-gallery__caption"
            v-if="item.text || item.yearTaken"
          >
            <p
              class="media-gallery__caption-text"
              v-if="item.text"
              v-text="item.text"
            />
            <span
              class="media-gallery__caption-year"
              v-if="item.yearTaken"
              v-text="item.yearTaken"
            />
          </figcaption>
        </figure>
      </splide-slide>
      <splide-slide v-for="i in pages" :key="`pdf-page-${i}`">
        <ix-img
          fixed
          v-if="imageUrl"
          :imgix-params="{
            h: maxImageSize,
            page: pages ? i : null,
            w: maxImageSize,
          }"
          :src="imageUrl"
        />
      </splide-slide>
    </splide>

    <Loader :loading="true" />

    <splide
      class="thumbnails"
      v-if="!pages && shouldShowThumbnails"
      ref="secondary"
      :options="splideThumbnailOptions"
      @splide:click="onThumbnailClick"
    >
      <splide-slide
        v-for="(src, index) in thumbnails"
        :key="`thumb-${index}-${src}`"
      >
        <ix-img
          :imgix-params="{
            crop: 'faces,edges',
            fit: 'crop',
            h: thumbnailSize,
            w: thumbnailSize,
          }"
          :src="src"
        />
      </splide-slide>
    </splide>
  </div>
</template>

<script>
  import Loader from "Components/loader/Loader.vue";
  import { Splide, SplideSlide } from "Components/splide";
  import { mapActions } from "vuex";
  import ImgixVideo from "./ImgixVideo.vue";

  export default {
    components: {
      ImgixVideo,
      Loader,
      Splide,
      SplideSlide,
    },

    computed: {
      options() {
        return {
          ...{
            arrows: this.showUI(),
            height: "70vh",
            pagination: false,
            rewind: true,
          },
          ...this.splideOptions,
        };
      },

      shouldShowThumbnails() {
        return this.showThumbnails && this.showUI();
      },

      thumbnails() {
        return [
          ...this.videos.map(({ thumbnail }) => thumbnail),
          ...this.items.map(({ src }) => src),
        ];
      },
    },

    data() {
      const thumbnailSize = 75;

      return {
        maxImageSize: 1750,
        splideThumbnailOptions: {
          arrows: false,
          cover: true,
          fixedWidth: thumbnailSize,
          height: thumbnailSize,
          isNavigation: true,
          pagination: false,
          rewind: true,
          updateOnMove: true,
        },
        thumbnailSize,
      };
    },

    methods: {
      ...mapActions({ toggleModal: "modal/toggle" }),

      onSplideMoved(_splide, index) {
        // Pause any videos when viewing a new slide.
        for (const ref of this.$refs.video ?? []) {
          ref.pause();
        }

        // Sync the thumbnail strip's active slide to the main view. Splide
        // scrolls the strip automatically to keep the active thumb visible.
        const { secondary } = this.$refs;
        if (secondary && secondary.splide.index !== index) {
          secondary.splide.go(index);
        }
      },

      onThumbnailClick(_splide, slide) {
        const { primary } = this.$refs;
        if (primary && primary.splide.index !== slide.index) {
          primary.splide.go(slide.index);
        }
      },

      showUI() {
        if (this.pages) {
          return this.pages > 1;
        }

        return this.items.length + this.videos.length > 1;
      },
    },

    mounted() {
      // Vue runs this hook before the `v-show` on the modal sets `display`, so
      // Splide has just measured every slide as zero wide and translated to
      // slide 0 regardless of the start index. It only re-measures on window
      // resize, so nudge it once the modal is actually visible.
      this.$nextTick(() => {
        this.$refs.primary?.splide?.refresh();
      });
    },
    name: "MediaGallery",

    props: {
      imageUrl: {
        default: "",
        type: String,
      },
      // Structured image entries `{ src, text, yearTaken }`. Each renders with a
      // caption bar beneath it whenever it carries a caption or a year.
      items: {
        default: () => [],
        type: Array,
      },
      pages: {
        default: 0,
        type: Number,
      },
      showThumbnails: {
        default: false,
        type: Boolean,
      },
      splideOptions: {
        default: () => ({}),
        type: Object,
      },
      videos: {
        default: () => [],
        type: Array,
      },
    },
  };
</script>
